import { ServerAnalysisItem, ServerCategorizationsData } from "../models/data-structures";
import {
  HtmlElementsSummary,
  UrlScanCertificate,
  UrlScanResource,
  UrlScanResourceError,
  UrlScanResponse,
} from "../models/url-analysis-data-structures";

export function findHtmlElementsObject(
  objects: ServerAnalysisItem[] | undefined,
): ServerAnalysisItem | undefined {
  if (!Array.isArray(objects)) return undefined;
  return objects.find(
    (o) => o.ObjectType === "HTML" && !!o.MetaData?.HTMLElements,
  );
}

// Adapter that lets components built for file reports (IndicatorsOverviewsLayoutCard,
// ObjectTreeStruct) be reused unmodified against a single resource's analysis.
export function toServerCategorizationsData(
  analysis: UrlScanResource["analysis"],
  syntheticAnalysisId: string,
): ServerCategorizationsData | null {
  if (!analysis) return null;
  return {
    ...analysis,
    AnalysisId: syntheticAnalysisId,
  };
}

export function extractDomain(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

export type ResourceStatus = "ok" | "error" | "no-analysis";

export function getResourceStatus(resource: UrlScanResource): ResourceStatus {
  if (resource.error) return "error";
  if (!resource.analysis) return "no-analysis";
  return "ok";
}

function countIndicators(analysis: UrlScanResource["analysis"]): number {
  if (!analysis || !Array.isArray(analysis.Objects)) return 0;
  return analysis.Objects.reduce(
    (sum, obj) =>
      sum +
      (obj.IndicatorCategories || []).reduce(
        (catSum, cat) => catSum + (cat.Indicators?.length || 0),
        0,
      ),
    0,
  );
}

// Prefers the scanned object's own size (available whenever the resource
// was analysed), falling back to the HTTP "Content-Length" response header
// (case-insensitive, since header casing isn't guaranteed) for resources
// that weren't analysed (e.g. plain images/fonts with no engine coverage).
function getResourceSize(resource: UrlScanResource): number | null {
  const objectSize = resource.analysis?.Objects?.[0]?.ObjectSize;
  if (typeof objectSize === "number" && !Number.isNaN(objectSize)) {
    return objectSize;
  }
  const headers = resource.response?.headers;
  if (!headers) return null;
  const contentLengthKey = Object.keys(headers).find(
    (key) => key.toLowerCase() === "content-length",
  );
  if (!contentLengthKey) return null;
  const parsed = parseInt(headers[contentLengthKey], 10);
  return Number.isNaN(parsed) ? null : parsed;
}

export interface UrlPrimaryViewModel {
  requestedUrl: string;
  httpStatus: number | null;
  contentType: string | null;
  sha256: string | null;
  size: number | null;
  certificate: UrlScanCertificate | null;
  ratingFlag: string | null;
  description: string | null;
  timestamp: string | null;
  scanTimeMs: number | null;
  rating: number | null;
  htmlSummary: HtmlElementsSummary | null;
  analysisForReuse: ServerCategorizationsData | null;
  error: UrlScanResourceError | null;
}

export interface UrlSubResourceRow {
  key: string;
  url: string;
  domain: string;
  status: ResourceStatus;
  httpStatus: number | null;
  contentType: string | null;
  sha256: string | null;
  size: number | null;
  objectType: string | null;
  rating: number | null;
  ratingFlag: string | null;
  indicatorCount: number;
  analysisForReuse: ServerCategorizationsData | null;
  raw: UrlScanResource;
}

export interface UrlScanViewModel {
  scanId: string;
  overallRating: number;
  primary: UrlPrimaryViewModel;
  subResources: UrlSubResourceRow[];
}

export function transformUrlScanResult(raw: UrlScanResponse): UrlScanViewModel {
  const resources = Array.isArray(raw.resources) ? raw.resources : [];
  const primaryResource = resources[0];
  const scanId = raw.id || "url-analysis";

  const primary: UrlPrimaryViewModel = primaryResource
    ? {
        requestedUrl: primaryResource.url,
        httpStatus: primaryResource.response?.status ?? null,
        contentType: primaryResource.response?.content_type ?? null,
        sha256: primaryResource.response?.sha256 ?? null,
        size: getResourceSize(primaryResource),
        certificate: primaryResource.response?.certificate ?? null,
        ratingFlag:
          primaryResource.analysis?.SummaryDescription?.RatingFlag ?? null,
        description:
          primaryResource.analysis?.SummaryDescription?.Description ?? null,
        timestamp: primaryResource.analysis?.TimeStamp ?? null,
        scanTimeMs: primaryResource.analysis?.ScanTime ?? null,
        rating: primaryResource.analysis?.Rating ?? null,
        htmlSummary:
          (findHtmlElementsObject(primaryResource.analysis?.Objects)?.MetaData
            ?.HTMLElements as HtmlElementsSummary | undefined) ?? null,
        analysisForReuse: toServerCategorizationsData(
          primaryResource.analysis,
          scanId,
        ),
        error: primaryResource.error,
      }
    : {
        requestedUrl: "",
        httpStatus: null,
        contentType: null,
        sha256: null,
        size: null,
        certificate: null,
        ratingFlag: null,
        description: null,
        timestamp: null,
        scanTimeMs: null,
        rating: null,
        htmlSummary: null,
        analysisForReuse: null,
        error: {
          kind: "empty",
          message: "No resources were returned for this scan.",
        },
      };

  // Includes the primary resource (index 0, the URL that was actually
  // submitted) alongside every sub-resource, so the table is a complete
  // inventory of everything involved in the page load, not just the
  // sub-resources. The primary is also shown in detail above via
  // UrlOverviewCard/UrlPageMetadataCard, but it should still be findable
  // and selectable from the resource list like any other resource.
  const subResources: UrlSubResourceRow[] = resources
    .map((resource, i) => {
      const primaryObject = resource.analysis?.Objects?.[0];
      // Prefer the final URL actually served (post-redirects) for display;
      // fall back to the requested URL for resources with no response
      // (e.g. failed fetches).
      const displayUrl = resource.response?.url ?? resource.url;
      return {
        key: `${i}-${resource.url}`,
        url: displayUrl,
        domain: extractDomain(displayUrl),
        status: getResourceStatus(resource),
        httpStatus: resource.response?.status ?? null,
        contentType: resource.response?.content_type ?? null,
        sha256: resource.response?.sha256 ?? null,
        size: getResourceSize(resource),
        objectType: primaryObject?.ObjectType ?? null,
        rating: resource.analysis?.Rating ?? null,
        ratingFlag: resource.analysis?.SummaryDescription?.RatingFlag ?? null,
        indicatorCount: countIndicators(resource.analysis),
        analysisForReuse: toServerCategorizationsData(
          resource.analysis,
          `${scanId}-${i}`,
        ),
        raw: resource,
      };
    })
    .sort((a, b) => (b.rating ?? -1) - (a.rating ?? -1));

  return {
    scanId,
    overallRating: raw.rating,
    primary,
    subResources,
  };
}
